import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from '../../TokenAwareTextField';
import { useFetchAssessments } from '../../../../../../hooks/assessment-hooks';
import { Assessment, ASSESSMENT_PURPOSES } from '../../../../../../support/assessments';

const PURPOSE_LABELS = new Map(ASSESSMENT_PURPOSES.map((p) => [p.value, p.label]));

/**
 * Searchable picker over the reusable-assessment catalogue (react-query). Emits the numeric
 * assessment id (or null when cleared). The assessment's purpose is shown as a secondary label.
 */
export function AssessmentPicker(props: {
  value: number | null;
  onChange: (id: number | null) => void;
  disabled?: boolean;
}): JSX.Element {
  const { value, onChange, disabled } = props;
  const { data, isLoading } = useFetchAssessments();

  const options = useMemo<Assessment[]>(() => data ?? [], [data]);

  const selected = useMemo(
    () => (value != null ? options.find((a) => a.id === value) ?? null : null),
    [options, value]
  );

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      loading={isLoading}
      options={options}
      value={selected}
      onChange={(_e, next) => onChange(next?.id ?? null)}
      getOptionLabel={(a) => a.name || `Assessment #${a.id}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(liProps, a) => (
        <Box component="li" {...liProps} key={a.id}>
          <Box>
            <Typography variant="body2">{a.name || `Assessment #${a.id}`}</Typography>
            {a.purpose && (
              <Typography variant="caption" color="text.secondary">
                {PURPOSE_LABELS.get(a.purpose) ?? a.purpose}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} label="Assessment" placeholder="Search assessments…" />
      )}
    />
  );
}
